import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertCircle, Check, Clock, X, ListChecks } from "lucide-react";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
const SECRET_STORAGE_KEY = "founding_admin_secret_v1";

type FoundingStatus = "pending" | "approved" | "rejected" | "waitlist";

interface Application {
  id: number;
  name: string;
  email: string;
  podcast: string;
  cadence: string;
  reason: string | null;
  status: FoundingStatus;
  submittedAt: string;
  reviewedAt: string | null;
  grantedUserId: string | null;
}

interface ListResponse {
  applications: Application[];
  approvedCount: number;
  cap: number;
  isFull: boolean;
}


export default function AdminFoundingPage() {
  const [secret, setSecret] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return window.sessionStorage.getItem(SECRET_STORAGE_KEY) ?? "";
  });
  const [secretInput, setSecretInput] = useState<string>("");
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Founding admin · Podcasts Abbreviated";
    return () => {
      meta.remove();
      document.title = prevTitle;
    };
  }, []);

  const load = useCallback(async (theSecret: string) => {
    if (!theSecret) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${basePath}/api/admin/founding`, {
        headers: { "X-Admin-Secret": theSecret },
      });
      if (res.status === 401) {
        setError("That secret didn't work. Try again.");
        setData(null);
        window.sessionStorage.removeItem(SECRET_STORAGE_KEY);
        setSecret("");
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(typeof body?.error === "string" ? body.error : `Request failed (${res.status})`);
        return;
      }
      const json = (await res.json()) as ListResponse;
      setData(json);
    } catch {
      setError("Couldn't reach the server.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (secret) void load(secret);
  }, [secret, load]);

  function handleSecretSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = secretInput.trim();
    if (!trimmed) return;
    window.sessionStorage.setItem(SECRET_STORAGE_KEY, trimmed);
    setSecret(trimmed);
    setSecretInput("");
  }

  async function act(id: number, action: "approve" | "reject" | "waitlist") {
    if (!secret) return;
    setBusyId(id);
    setError(null);
    try {
      const res = await fetch(`${basePath}/api/admin/founding/${id}/${action}`, {
        method: "POST",
        headers: { "X-Admin-Secret": secret },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(typeof body?.error === "string" ? body.error : `Action failed (${res.status})`);
        return;
      }
      await load(secret);
    } catch {
      setError("Couldn't reach the server.");
    } finally {
      setBusyId(null);
    }
  }

  function handleSignOut() {
    window.sessionStorage.removeItem(SECRET_STORAGE_KEY);
    setSecret("");
    setData(null);
  }

  if (!secret) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-border/60 bg-card/40">
          <CardContent className="p-6 sm:p-8">
            <h1 className="text-xl font-semibold mb-1">Founding admin</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Paste your <code className="font-mono text-xs">ADMIN_SECRET</code> to review applications. The secret is held in this tab only and never written to disk.
            </p>
            <form onSubmit={handleSecretSubmit} className="space-y-4">
              <div>
                <Label htmlFor="admin-secret" className="font-mono uppercase text-[11px] tracking-wider text-muted-foreground mb-2 block">
                  Admin secret
                </Label>
                <Input
                  id="admin-secret"
                  type="password"
                  autoComplete="off"
                  value={secretInput}
                  onChange={(e) => setSecretInput(e.target.value)}
                  placeholder="••••••••••••"
                />
              </div>
              {error && (
                <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-3">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              <Button type="submit" className="w-full font-mono uppercase tracking-wider">
                Continue
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <ListChecks size={20} className="text-primary" />
              Founding applications
            </h1>
            {data && (
              <p className="text-sm text-muted-foreground mt-1">
                {data.approvedCount} of {data.cap} spots approved · {data.isFull ? "program is full (waitlist mode)" : `${data.cap - data.approvedCount} remaining`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void load(secret)}
              disabled={loading}
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : "Refresh"}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleSignOut}>
              Forget secret
            </Button>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-3 mb-4">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 size={20} className="animate-spin mr-2" /> Loading…
          </div>
        ) : !data || data.applications.length === 0 ? (
          <Card className="border-border/60 bg-card/40">
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              No applications yet.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {data.applications.map((app) => (
              <ApplicationRow
                key={app.id}
                app={app}
                busy={busyId === app.id}
                onAct={(action) => act(app.id, action)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function statusVariant(status: FoundingStatus): {
  label: string;
  className: string;
  Icon: typeof Check;
} {
  switch (status) {
    case "approved":
      return { label: "Approved", className: "bg-primary/15 text-primary border-primary/30", Icon: Check };
    case "rejected":
      return { label: "Rejected", className: "bg-destructive/10 text-destructive border-destructive/30", Icon: X };
    case "waitlist":
      return { label: "Waitlist", className: "bg-amber-500/10 text-amber-400 border-amber-500/30", Icon: Clock };
    default:
      return { label: "Pending", className: "bg-muted text-muted-foreground border-border", Icon: Clock };
  }
}

function ApplicationRow({
  app,
  busy,
  onAct,
}: {
  app: Application;
  busy: boolean;
  onAct: (action: "approve" | "reject" | "waitlist") => void;
}) {
  const variant = statusVariant(app.status);
  const Icon = variant.Icon;
  const submitted = new Date(app.submittedAt).toLocaleString();

  return (
    <Card className="border-border/60 bg-card/40">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-semibold truncate">{app.name}</h2>
              <Badge variant="outline" className={`font-mono text-[10px] uppercase tracking-wider ${variant.className}`}>
                <Icon size={11} className="mr-1" />
                {variant.label}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground font-mono break-all">{app.email}</p>
          </div>
          <div className="text-xs text-muted-foreground font-mono shrink-0">
            #{app.id} · {submitted}
          </div>
        </div>

        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
          <div>
            <dt className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Podcast</dt>
            <dd className="text-foreground/90 break-words">{app.podcast}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Cadence</dt>
            <dd className="text-foreground/90 break-words">{app.cadence}</dd>
          </div>
          {app.reason && (
            <div className="sm:col-span-2">
              <dt className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Why</dt>
              <dd className="text-foreground/90 whitespace-pre-wrap break-words">{app.reason}</dd>
            </div>
          )}
          {app.grantedUserId && (
            <div className="sm:col-span-2">
              <dt className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Granted to user</dt>
              <dd className="text-foreground/90 font-mono text-xs">{app.grantedUserId}</dd>
            </div>
          )}
        </dl>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            disabled={busy || app.status === "approved"}
            onClick={() => onAct("approve")}
            className="font-mono uppercase tracking-wider"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : "Approve"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy || app.status === "waitlist"}
            onClick={() => onAct("waitlist")}
          >
            Waitlist
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy || app.status === "rejected"}
            onClick={() => onAct("reject")}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            Reject
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
